import { useQuery } from "@tanstack/react-query";
import api from "services/api";
import { StyledInputLabel, StyledSelect, StyledTextField, Title, Typography } from "./styled";
import { Select, TextField } from "v2/components";
import {
  MenuItem,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  InputAdornment,
  FormControl,
  Modal,
  Box,
  Button,
  IconButton,
} from "@mui/material";

import GoogleDriveIcon from "components/svg/google-drive-icon";
import ESocialIcon from "components/svg/e-social-icon";
import { IConnector } from "../../CreateWorkspaces";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { AppQbemApiView } from "v2/pages/Workspace/Activity/AppQbemApi/AppQbemApi.View";

// Display-name match used to route QBem activity connectors to the dedicated
// credentials form. Display names are user-visible and may drift; once the API
// response exposes a stable connectorId/slug here, prefer matching by that.
const QBEM_CONNECTOR_NAME = "QBem";

interface IActivityConnector {
  id: string;
  workspaceActivityId?: string;
  connectorName: string;
  type: string;
  config: Record<string, unknown>;
}

interface IProps {
  connectors: IConnector[];
  selectedConnectors: string[];
  activityConnectors?: IActivityConnector[];
  onConfigSaved?: () => void;
  workspaceId?: string;
}

export function ConnectorSetup({ connectors, selectedConnectors, activityConnectors, onConfigSaved, workspaceId }: IProps) {
  const [selectedFilter, setSelectedFilter] = useState("TODOS");
  const [search, setSearch] = useState("");
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [qbemModalOpen, setQbemModalOpen] = useState(false);
  const [selectedActivityConnector, setSelectedActivityConnector] = useState<IActivityConnector | null>(null);
  const [configJson, setConfigJson] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const handleOpenConfig = (activityConnector: IActivityConnector) => {
    if (activityConnector.connectorName === QBEM_CONNECTOR_NAME) {
      setQbemModalOpen(true);
      return;
    }
    setSelectedActivityConnector(activityConnector);
    const hasConfig = activityConnector.config && Object.keys(activityConnector.config).length > 0;
    setConfigJson(hasConfig ? JSON.stringify(activityConnector.config, null, 2) : "");
    setSaveError("");
    setConfigModalOpen(true);
  };

  const handleSaveConfig = async () => {
    if (!selectedActivityConnector) return;

    try {
      JSON.parse(configJson);
    } catch {
      setSaveError("JSON invalido. Verifique a formatacao.");
      return;
    }

    setSaving(true);
    setSaveError("");

    try {
      await api.put(`/v2/workspace-activity-connectors/${selectedActivityConnector.id}/config`, {
        config: JSON.parse(configJson),
      });
      setConfigModalOpen(false);
      onConfigSaved?.();
    } catch (err: any) {
      setSaveError(err?.response?.data?.data?.message || "Erro ao salvar configuracao.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Title>Conectores</Title>
        <Typography>
          Os conectores abaixo são essenciais para iniciar as atividades
          configuradas.XX
        </Typography>
      </div>
      <StyledTextField
        value={search}
        placeholder="Pesquisar"
        label="Conector"
        onChange={(e) => setSearch(e.target.value.toLowerCase())}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                color="action"
                style={{ width: "24px", aspectRatio: "1" }}
              />
            </InputAdornment>
          ),
        }}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <FormControl style={{ width: "230px" }} >
          <StyledInputLabel id="tipo-connector">Atividade relacionada</StyledInputLabel>
          <StyledSelect
            labelId="tipo-connector"
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value as string)}
            style={{ height: "55.3px", marginBottom: "2.7px" }}
          >
            <MenuItem value="TODOS">Todos</MenuItem>
          </StyledSelect>
        </FormControl>
      </div>
      <TableContainer
        sx={{
          "& .MuiTableCell-root": { background: "#FFFFFF !important" },
        }}
      >
        <Table>
          <TableRow style={{ height: "70px" }}>
            <TableCell style={{ width: "300px" }}>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "rgb(111, 111, 120)",
                  fontWeight: "600",
                }}
              >
                Nome
              </span>
            </TableCell>
            <TableCell style={{ width: "250px" }}>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "rgb(111, 111, 120)",
                  fontWeight: "600",
                }}
              >
                Atividade relacionada
              </span>
            </TableCell>
            <TableCell style={{ width: "150px" }}>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "rgb(111, 111, 120)",
                  fontWeight: "600",
                }}
              >
                Status
              </span>
            </TableCell>
            {activityConnectors?.some(ac => ac.type === "INPUT") && (
              <TableCell style={{ width: "120px" }}>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "rgb(111, 111, 120)",
                    fontWeight: "600",
                  }}
                >
                  Ações
                </span>
              </TableCell>
            )}
          </TableRow>
          {[...new Set(selectedConnectors)].filter(conector => !search ? true : conector.toLowerCase().includes(search))?.map((connector) => {
            const activityConn = activityConnectors?.find(ac => ac.connectorName === connector && ac.type === "INPUT");
            const hasConfig = activityConn?.config && Object.keys(activityConn.config).length > 0;

            return (
              <TableRow key={connector} style={{ height: "70px" }}>
                <TableCell>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "35px",
                        height: "35px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "solid 1px #a6a6a6",
                        borderRadius: "1000px",
                        padding: "6px",
                      }}
                    >
                      {connector === "Google Drive" && <GoogleDriveIcon />}
                      {connector === "eSocial" && <ESocialIcon />}
                    </div>
                    <span>{connector}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Chip
                      label="Carregar e corrigir dados"
                      variant="outlined"
                    />
                    <Chip label="Executar processo" variant="outlined" />
                  </div>
                </TableCell>
                <TableCell>
                  {!!connectors.find(
                    (orgConnector) =>
                      orgConnector.name === connector &&
                      !orgConnector.isExpired
                  ) && (!activityConn || hasConfig) ? (
                    <div
                      style={{
                        color: "#2E7D32",
                        backgroundColor: "#07e71213",
                        opacity: "0.8",
                        padding: "0.25rem 0.75rem",
                        borderRadius: "0.75rem",
                        fontWeight: "400",
                        fontSize: "12px",
                        lineHeight: "16px",
                        width: "fit-content",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <span>{hasConfig ? "Configurado" : "Conectado"}</span>
                    </div>
                  ) : (
                    <div
                      style={{
                        color: "#EF6C00",
                        backgroundColor: "#ff730013",
                        padding: "0.25rem 0.75rem",
                        borderRadius: "0.75rem",
                        fontWeight: "400",
                        fontSize: "12px",
                        lineHeight: "16px",
                        width: "fit-content",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <span>Pendente</span>
                    </div>
                  )}
                </TableCell>
                {activityConnectors?.some(ac => ac.type === "INPUT") && (
                  <TableCell>
                    {activityConn && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => handleOpenConfig(activityConn)}
                        sx={{
                          textTransform: "none",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                      >
                        Configurar
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </Table>
      </TableContainer>

      {/* QBem API Config Modal */}
      <Modal open={qbemModalOpen} onClose={() => setQbemModalOpen(false)}>
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          maxWidth: 700,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: "1rem",
          outline: "none",
          padding: "24px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <span style={{ fontSize: "18px", fontWeight: 600 }}>Configurar conector: QBem</span>
            <IconButton onClick={() => setQbemModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </div>
          <AppQbemApiView
            workspaceId={workspaceId}
            isModal
            onSave={() => { setQbemModalOpen(false); onConfigSaved?.(); }}
          />
        </Box>
      </Modal>



      {/* Config Modal */}
      <Modal open={configModalOpen} onClose={() => setConfigModalOpen(false)}>
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          maxWidth: 700,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: "1rem",
          outline: "none",
          padding: "24px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <span style={{ fontSize: "18px", fontWeight: 600 }}>
              Configurar conector: {selectedActivityConnector?.connectorName}
            </span>
            <IconButton onClick={() => setConfigModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </div>
          <p style={{ color: "#666", fontSize: "14px", margin: "0 0 16px 0" }}>
            Cole abaixo o JSON de configuracao do conector (autenticacao, endpoint, mapeamento de campos).
          </p>
          <textarea
            value={configJson}
            onChange={(e) => { setConfigJson(e.target.value); setSaveError(""); }}
            style={{
              width: "100%",
              minHeight: "350px",
              fontFamily: "monospace",
              fontSize: "13px",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              resize: "vertical",
              boxSizing: "border-box",
            }}
            placeholder='{"providerType": "standard", "baseUrl": "...", "apiKey": "...", ...}'
          />
          {saveError && (
            <p style={{ color: "#d32f2f", fontSize: "13px", margin: "8px 0 0 0" }}>{saveError}</p>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "16px" }}>
            <Button
              variant="outlined"
              onClick={() => setConfigModalOpen(false)}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveConfig}
              disabled={saving || !configJson.trim()}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
}
